import { useState, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { Wand2, Loader2, GraduationCap, Sparkles, Brain, Globe, ExternalLink, X, Link, PenLine, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { ContentFormData, CreationMode } from '..';
import { useToast } from '@/hooks/use-toast';
import { api } from '@/lib/api';

interface ImportThreadResponse {
  topic: string;
  content: string;
  source: string;
  source_url?: string;
  title?: string;
  notes?: string[];
  recommended_num_scenes?: number;
}

// Pre-defined topic suggestions
const topicOptions = [
  { id: 'educational', name: 'Educational', prompt: 'Create an informative video explaining how blockchain technology works in simple terms.', icon: GraduationCap },
  { id: 'motivation', name: 'Motivation', prompt: 'Produce an inspiring video about overcoming obstacles and achieving personal goals.', icon: Sparkles },
  { id: 'philosophy', name: 'Philosophy', prompt: 'Develop a thought-provoking video discussing existentialism and its relevance to modern life.', icon: Brain },
  { id: 'fitness', name: 'Fitness', prompt: 'Create a workout tutorial showing effective exercises that can be done at home without equipment.', icon: Sparkles },
  { id: 'technology', name: 'Technology', prompt: 'Explain the impact of artificial intelligence on the future of work and daily life.', icon: Brain },
  { id: 'cooking', name: 'Cooking', prompt: 'Share a simple but impressive recipe that beginners can make to impress their friends.', icon: GraduationCap },
];

// Thread source types
const threadSources: {
  id: string;
  name: string;
  icon: () => ReactNode;
  placeholder: string;
}[] = [
  {
    id: 'twitter',
    name: 'X / Twitter',
    icon: () => (
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
    placeholder: 'Paste an X/Twitter thread URL or thread content...',
  },
  {
    id: 'website',
    name: 'Blog',
    icon: () => <Globe className="h-4 w-4" />,
    placeholder: 'Paste a public article URL (Substack, Medium, your own site…)',
  },
];

/** Playful sample slugs shown when the Blog source is active — gives users a feel for the URL shape. */
const BLOG_SAMPLE_URLS = [
  'goblinmode.substack.com/p/touched-grass-once-would-not-recommend',
  'dril.substack.com/p/strong-opinions-about-soup',
  'bigbrain.substack.com/p/why-tabs-beat-spaces-forever',
  'procrastinator.substack.com/p/how-i-wrote-this-instead-of-working',
  'existential.substack.com/p/are-pigeons-real-an-investigation',
  'medium.com/@you/i-quit-my-job-to-rate-cheeses-full-time-abc123',
];

// Creation mode cards (upload disabled until the flow is built)
const creationModes: {
  id: CreationMode;
  name: string;
  description: string;
  icon: React.ElementType;
  disabled?: boolean;
}[] = [
  {
    id: 'import',
    name: 'Import',
    description: 'Import from a URL or thread',
    icon: Link,
  },
  {
    id: 'custom',
    name: 'Custom',
    description: 'Write your own prompt',
    icon: PenLine,
  },
  {
    id: 'upload',
    name: 'Upload',
    description: 'Upload images & script',
    icon: Upload,
  },
];

interface TopicSelectProps {
  formData: ContentFormData;
  updateFormData: (data: Partial<ContentFormData>) => void;
  onValidationChange: (isValid: boolean) => void;
}

export function TopicSelect({ formData, updateFormData, onValidationChange }: TopicSelectProps) {
  const [activeMode, setActiveMode] = useState<CreationMode | null>(() =>
    formData.creationMode ?? 'custom'
  );
  const [promptText, setPromptText] = useState(formData.customPrompt || '');
  const [threadInput, setThreadInput] = useState('');
  const [selectedSource, setSelectedSource] = useState<string | null>(null);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importedSource, setImportedSource] = useState<{ url: string; type: string; recommendedScenes?: number } | null>(
    formData.sourceUrl ? { url: formData.sourceUrl, type: formData.sourceType || '' } : null
  );
  const { toast } = useToast();

  // Default to the custom-prompt mode so the step starts ready to type into
  useEffect(() => {
    if (!formData.creationMode) updateFormData({ creationMode: 'custom' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.creationMode]);

  // Validation — upload mode is valid as soon as it's picked (media is added on
  // the next step); other modes need prompt text.
  useEffect(() => {
    if (formData.creationMode === 'upload') {
      onValidationChange(true);
      return;
    }
    onValidationChange(promptText.trim().length > 0);
  }, [promptText, formData.creationMode, onValidationChange]);

  const handleModeSelect = (mode: CreationMode) => {
    setActiveMode(mode);
    updateFormData({ creationMode: mode });
  };

  // --- Import flow ---
  const handleSourceSelect = (sourceId: string) => {
    if (selectedSource === sourceId) {
      setSelectedSource(null);
      setThreadInput('');
    } else {
      setSelectedSource(sourceId);
      setThreadInput('');
    }
  };

  const handleImportThread = async () => {
    if (!threadInput.trim() || !selectedSource) return;
    setIsImporting(true);

    try {
      const response = await api.post<ImportThreadResponse>('/content/import-thread', {
        thread: threadInput.trim(),
        source: selectedSource,
      });

      const data = response.data;
      const importedText = data.content;

      if (importedText && importedText.trim()) {
        const importTitle = data.title || importedText.split('.')[0];
        setPromptText(importedText);
        updateFormData({
          topic: data.topic || 'custom',
          title: importTitle,
          customPrompt: importedText,
          topicName: importTitle,
          sourceUrl: data.source_url || undefined,
          sourceType: data.source || selectedSource,
        });

        if (data.source_url) {
          setImportedSource({ url: data.source_url, type: data.source || selectedSource, recommendedScenes: data.recommended_num_scenes });
        }

        setThreadInput('');
        setSelectedSource(null);

        if (data.notes && data.notes.length > 0) {
          data.notes.forEach((note) => {
            toast({ title: 'Import Note', description: note });
          });
        }

        toast({
          title: 'Thread Imported',
          description: data.title ? `"${data.title}" has been added to your prompt.` : 'Thread content has been added to your prompt.',
        });
      } else {
        toast({ title: 'Import Failed', description: 'Could not extract content from the thread. Try pasting the text directly.', variant: 'destructive' });
      }
    } catch (error: any) {
      console.error('Error importing thread:', error.response?.data || error);
      if (error.response?.status === 401) {
        toast({ title: 'Authentication Error', description: 'Please log in again to continue.', variant: 'destructive' });
      } else {
        let errorMessage = 'Failed to import thread. Please try again.';
        const detail = error.response?.data?.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail) && detail.length > 0) {
          errorMessage = detail.map((d: any) => d.msg || JSON.stringify(d)).join(', ');
        }
        toast({ title: 'Import Failed', description: errorMessage, variant: 'destructive' });
      }
    } finally {
      setIsImporting(false);
    }
  };

  // --- Custom flow ---
  const handlePromptChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setPromptText(event.target.value);
  };

  const handlePromptBlur = () => {
    updateFormData({ 
      topic: 'custom', 
      customPrompt: promptText.trim() ? promptText : undefined,
      topicName: promptText.trim() ? promptText.split('.')[0] : undefined,
    });
  };

  const appendTopicPrompt = (topicId: string) => {
    const selectedTopic = topicOptions.find(option => option.id === topicId);
    if (selectedTopic) {
      setPromptText(selectedTopic.prompt);
      updateFormData({ topic: topicId, customPrompt: selectedTopic.prompt, topicName: selectedTopic.name });
    }
  };

  const handleEnhanceWithAI = async () => {
    if (!promptText.trim()) return;
    setIsEnhancing(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast({ title: 'Authentication Error', description: 'Please log in again to continue.', variant: 'destructive' });
        return;
      }

      const response = await api.post('/ai/enhance-prompt', 
        { topic: formData.topic || 'custom', platform: formData.platform, prompt: promptText },
        { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );

      const enhancedPrompt = response.data.enhanced_prompt;
      setPromptText(enhancedPrompt);
      updateFormData({ 
        topic: formData.topic || 'custom',
        customPrompt: enhancedPrompt,
        enhancedPrompt: enhancedPrompt,
        topicName: formData.topicName || enhancedPrompt.split('.')[0],
      });
      toast({ title: 'Prompt Enhanced', description: 'Your content prompt has been enhanced!' });
    } catch (error: any) {
      console.error('Error enhancing prompt:', error.response?.data || error);
      if (error.response?.status === 401) {
        toast({ title: 'Authentication Error', description: 'Please log in again to continue.', variant: 'destructive' });
      } else {
        toast({
          title: 'Enhancement Failed',
          description: error.response?.data?.detail === 'Service temporarily unavailable. Please try again later.'
            ? 'Service is temporarily unavailable. Please try again in a few minutes.'
            : 'Failed to enhance prompt. Please try again.',
          variant: 'destructive',
        });
      }
    } finally {
      setIsEnhancing(false);
    }
  };

  const activeSource = threadSources.find(s => s.id === selectedSource);

  // Pick a stable random sample once per Blog selection so the example doesn’t flicker on rerenders.
  const blogSample = useMemo(
    () =>
      selectedSource === 'website'
        ? BLOG_SAMPLE_URLS[Math.floor(Math.random() * BLOG_SAMPLE_URLS.length)]
        : null,
    [selectedSource]
  );

  return (
    <div className="space-y-5">
      {/* Creation mode selector */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {creationModes.map((mode) => {
          const Icon = mode.icon;
          const isActive = activeMode === mode.id;
          const isDisabled = Boolean(mode.disabled);
          return (
            <button
              key={mode.id}
              type="button"
              disabled={isDisabled}
              aria-disabled={isDisabled}
              onClick={() => handleModeSelect(mode.id)}
              className={cn(
                "relative flex flex-col items-center gap-2 p-4 md:p-5 rounded-xl border-2 transition-all text-center",
                isDisabled && "opacity-50 cursor-not-allowed",
                !isDisabled &&
                  (isActive
                    ? "border-primary bg-primary/5 shadow-sm"
                    : "border-border bg-background hover:border-primary/50 cursor-pointer")
              )}
            >
              {isDisabled && (
                <span className="absolute top-2 right-2 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-muted text-muted-foreground">
                  Soon
                </span>
              )}
              <div
                className={cn(
                  "flex items-center justify-center h-10 w-10 rounded-full",
                  isActive && !isDisabled ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                )}
              >
                <Icon className="h-5 w-5" />
              </div>
              <div>
                <div className="text-sm font-semibold">{mode.name}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{mode.description}</div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Source reference badge (shown after successful import) */}
      {importedSource && (
        <div className="rounded-lg border border-primary/20 bg-primary/5 text-sm">
          <div className="flex items-center gap-2 px-3 py-2">
            <ExternalLink className="h-3.5 w-3.5 text-primary shrink-0" />
            <span className="text-muted-foreground">Imported from</span>
            {importedSource.url && /^https?:\/\//i.test(importedSource.url) ? (
              <a href={importedSource.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline truncate max-w-[300px]">
                {importedSource.url}
              </a>
            ) : importedSource.url ? (
              <span className="text-foreground truncate max-w-[300px]">{importedSource.url}</span>
            ) : (
              <span className="text-foreground">direct paste</span>
            )}
            <span className="px-1.5 py-0.5 rounded text-xs bg-muted text-muted-foreground">{importedSource.type}</span>
            <button type="button" onClick={() => { setImportedSource(null); updateFormData({ sourceUrl: undefined, sourceType: undefined }); }} className="ml-auto text-muted-foreground hover:text-foreground">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          {importedSource.recommendedScenes && (
            <div className="flex items-center gap-2 px-3 py-2 border-t border-primary/10 text-xs text-muted-foreground">
              <Sparkles className="h-3.5 w-3.5 text-primary shrink-0" />
              <span>Based on this content, we recommend <span className="font-semibold text-foreground">{importedSource.recommendedScenes} scenes</span> — you can adjust this in the Settings step.</span>
            </div>
          )}
        </div>
      )}

      {/* ===== IMPORT FLOW ===== */}
      {activeMode === 'import' && (
        <div className="space-y-4 p-4 md:p-5 border rounded-lg bg-card">
          {/* Source type selector */}
          <div className="flex gap-2">
            {threadSources.map((source) => {
              const Icon = source.icon;
              const isActive = selectedSource === source.id;
              return (
                <button
                  key={source.id}
                  type="button"
                  onClick={() => handleSourceSelect(source.id)}
                  className={cn(
                    "flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-all",
                    isActive
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border bg-background text-muted-foreground hover:border-primary/50 hover:text-foreground"
                  )}
                >
                  <Icon />
                  {source.name}
                </button>
              );
            })}
          </div>

          {blogSample && (
            <p className="text-xs text-muted-foreground">
              e.g. <code className="font-mono text-foreground/80">{blogSample}</code>
            </p>
          )}

          <Textarea
            value={threadInput}
            onChange={(e) => setThreadInput(e.target.value)}
            placeholder={activeSource?.placeholder || 'Select a source above to get started...'}
            disabled={!selectedSource}
            className={cn(
              "min-h-[80px] text-sm resize-none border-border focus:border-primary transition-opacity",
              !selectedSource && "opacity-50 cursor-not-allowed"
            )}
          />

          <Button variant="outline" size="sm" className="w-full" onClick={handleImportThread} disabled={isImporting || !threadInput.trim() || !selectedSource}>
            {isImporting ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Importing...</>
            ) : (
              <>{activeSource ? <activeSource.icon /> : <Globe className="h-4 w-4" />}<span className="ml-2">Import {activeSource?.name || 'Thread'}</span></>
            )}
          </Button>

          {/* Prompt preview after import — editable */}
          {promptText && (
            <div className="space-y-3 pt-3 border-t">
              <div className="text-xs font-medium text-muted-foreground uppercase">Imported Content</div>
              <Textarea
                value={promptText}
                onChange={handlePromptChange}
                onBlur={handlePromptBlur}
                className="min-h-[120px] text-sm resize-none border-border focus:border-primary"
              />
              <Button variant="secondary" size="sm" className="w-full" onClick={handleEnhanceWithAI} disabled={isEnhancing || !promptText.trim()}>
                {isEnhancing ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Enhancing...</>
                ) : (
                  <><Wand2 className="mr-2 h-4 w-4" />Enhance with AI</>
                )}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* ===== CUSTOM FLOW ===== */}
      {activeMode === 'custom' && (
        <div className="p-4 md:p-6 border rounded-lg bg-card space-y-5">
      <Textarea
        value={promptText}
        onChange={handlePromptChange}
        onBlur={handlePromptBlur}
        placeholder="Describe what you want to create, e.g., 'Explain cryptocurrency in simple terms' or 'Make a dance tutorial'"
        className="min-h-[150px] text-base resize-none border-border focus:border-primary"
      />
      
          <Button variant="secondary" size="lg" className="w-full bg-secondary/90 hover:bg-secondary text-foreground" onClick={handleEnhanceWithAI} disabled={isEnhancing || !promptText.trim()}>
        {isEnhancing ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Enhancing...</>
        ) : (
              <><Wand2 className="mr-2 h-4 w-4" />Enhance with AI</>
        )}
      </Button>
      
        <div className="flex flex-wrap gap-2 justify-start">
          {topicOptions.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => appendTopicPrompt(option.id)}
              className="px-3 py-1.5 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors text-sm flex items-center"
            >
              <option.icon className="h-3.5 w-3.5 mr-1.5" />
              {option.name}
            </button>
          ))}
        </div>
      </div>
      )}

      {/* Upload mode — media is added on the next step */}
      {activeMode === 'upload' && (
        <div className="flex flex-col items-center justify-center gap-2 py-10 text-center text-sm text-muted-foreground">
          <Upload className="h-7 w-7 opacity-40" />
          Continue to add your images and write a voiceover line for each.
        </div>
      )}

      {/* Nothing selected prompt */}
      {!activeMode && (
        <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
          Select a creation method above to get started
        </div>
      )}
    </div>
  );
}
